import { useEffect, useState } from "react";
import { Button } from "../common/Button";
import { Modal } from "../common/Modal";
import { Field } from "../common/Field";
import { teamApi } from "../../services/teamApi";
import { useToast } from "../../context/ToastContext";

/**
 * Hand a batch of hives to a team member (#288).
 *
 * Suggested after testing: "let the team members check multiple hives and
 * then choose Team Assign from the dropdown and then get a popup to
 * choose from the current team members". Exactly right, and it is where
 * somebody already is when they decide -- they have just been looking at
 * which hives need work.
 *
 * ONE TASK PER HIVE rather than one task naming several. A task is
 * something a person finishes, and "check hives 3, 7 and 11" cannot be
 * half-done in any way the app can record. Separate tasks also let the
 * ranking in #283 treat them separately, which matters when one of those
 * hives has an open issue and the others do not.
 */
export function TeamAssignDialog({ open, onClose, hiveIds, onAssigned }) {
  const { notify } = useToast();
  const [members, setMembers] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [taskType, setTaskType] = useState("Inspection");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    teamApi
      .listMembers()
      .then((r) => !cancelled && setMembers(r.members || []))
      .catch(() => !cancelled && setMembers([]));
    return () => { cancelled = true; };
  }, [open]);

  async function assign() {
    if (!assignee) return notify("Pick who it is for", "warning");
    if (!taskType.trim()) return notify("Say what the task is", "warning");

    setBusy(true);
    // allSettled, not Promise.all: if the fourth of five fails, the first
    // three were still assigned and telling somebody "it failed" would
    // send them to reassign work that already exists.
    const results = await Promise.allSettled(
      hiveIds.map((hiveId) =>
        teamApi.createTask({
          assigned_to_user_id: Number(assignee),
          task_type: taskType.trim(),
          due_date: dueDate || undefined,
          hive_id: hiveId,
        }),
      ),
    );
    setBusy(false);

    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed === 0) {
      notify(`Assigned ${hiveIds.length} ${hiveIds.length === 1 ? "task" : "tasks"}`, "success");
    } else if (failed === hiveIds.length) {
      notify("Couldn't assign those", "danger");
    } else {
      // The number that landed, because the next thing they do depends
      // on it.
      notify(`Assigned ${hiveIds.length - failed} of ${hiveIds.length} — try the rest again`, "warning");
    }
    if (failed < hiveIds.length) {
      onAssigned?.();
      onClose?.();
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Assign ${hiveIds.length} ${hiveIds.length === 1 ? "hive" : "hives"}`}
      footer={<Button onClick={assign} disabled={busy}>{busy ? "Assigning…" : "Assign"}</Button>}
    >
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        {/* Says what will happen, because "assign 5 hives" could mean one
            task or five and the difference matters to whoever receives
            them. */}
        <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
          One task per hive, so each can be finished on its own.
        </p>

        <Field label="Assign to">
          {(id, aria) => (
            <select id={id} {...aria} className="touch-target" value={assignee}
              onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Select a team member…</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.email}{m.role ? ` (${m.role})` : ""}
                </option>
              ))}
            </select>
          )}
        </Field>

        {members.length === 0 && (
          <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
            No team members yet — invite someone from the Team page first.
          </p>
        )}

        <Field label="What needs doing?">
          {(id, aria) => (
            <input id={id} {...aria} className="touch-target" value={taskType}
              onChange={(e) => setTaskType(e.target.value)} maxLength={100} />
          )}
        </Field>

        <Field label="Due (optional)">
          {(id, aria) => (
            <input id={id} {...aria} type="date" className="touch-target" value={dueDate}
              onChange={(e) => setDueDate(e.target.value)} />
          )}
        </Field>
      </div>
    </Modal>
  );
}
